import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Enhanced Portfolio Database Service
 * Handles all portfolio data operations including CRUD operations,
 * data synchronization, local storage management, and resume integration
 */
class PortfolioDatabase {
  constructor() {
    this.initialized = false;
    this.cache = {
      projects: [],
      skills: [],
      certifications: [],
      workHistory: [],
      education: [],
      profile: null,
      resumes: [], // Added resumes cache
      resumeTemplates: [], // Added resume templates
      lastSync: null
    };
    this.subscribers = {};
    this.syncInProgress = false;
  }

  /**
   * Initialize the database service
   */
  async initialize() {
    try {
      console.log('🔄 Initializing Enhanced Portfolio Database...');
      
      // Load cached data from AsyncStorage
      await this.loadFromStorage();
      
      // Initialize default data if empty
      await this.initializeDefaultData();
      
      // Set up auto-sync interval (every 30 seconds for resume editing)
      this.setupAutoSync();
      
      this.initialized = true;
      console.log('✅ Enhanced Portfolio Database initialized successfully');
      
      // Emit initialization event
      this.emit('database_initialized', { success: true });
      
      return true;
    } catch (error) {
      console.error('❌ Portfolio Database initialization failed:', error);
      this.emit('database_error', { error: error.message });
      return false;
    }
  }

  /**
   * Initialize default data for resume functionality
   */
  async initializeDefaultData() {
    try {
      // Initialize default profile if none exists
      if (!this.cache.profile) {
        this.cache.profile = {
          id: 'default_profile',
          name: '',
          email: '',
          phone: '',
          location: '',
          bio: '',
          linkedIn: '',
          website: '',
          github: '',
          profileImage: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }

      // Initialize sample data for demonstration if cache is empty
      if (this.cache.projects.length === 0) {
        this.cache.projects = [
          {
            id: 'sample_project_1',
            title: 'Portfolio Website',
            description: 'Personal portfolio website built with React and Node.js',
            technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
            status: 'completed',
            category: 'Web Development',
            startDate: '2023-01-01',
            endDate: '2023-03-01',
            url: 'https://example.com',
            achievements: ['Responsive design', 'SEO optimized', 'Fast loading'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
      }

      if (this.cache.skills.length === 0) {
        this.cache.skills = [
          {
            id: 'skill_1',
            name: 'JavaScript',
            proficiency: 85,
            category: 'Programming',
            experienceYears: 3,
            projects: ['sample_project_1'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          },
          {
            id: 'skill_2',
            name: 'React',
            proficiency: 80,
            category: 'Frontend',
            experienceYears: 2,
            projects: ['sample_project_1'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        ];
      }

      console.log('✅ Default data initialized');
    } catch (error) {
      console.error('❌ Error initializing default data:', error);
    }
  }

  /**
   * Load all data from AsyncStorage
   */
  async loadFromStorage() {
    try {
      const [projects, skills, certifications, workHistory, education, profile, resumes, resumeTemplates, lastSync] = await Promise.all([
        AsyncStorage.getItem('portfolio_projects'),
        AsyncStorage.getItem('portfolio_skills'),
        AsyncStorage.getItem('portfolio_certifications'),
        AsyncStorage.getItem('portfolio_work_history'),
        AsyncStorage.getItem('portfolio_education'),
        AsyncStorage.getItem('portfolio_profile'),
        AsyncStorage.getItem('portfolio_resumes'),
        AsyncStorage.getItem('portfolio_resume_templates'),
        AsyncStorage.getItem('portfolio_last_sync')
      ]);

      this.cache.projects = projects ? JSON.parse(projects) : [];
      this.cache.skills = skills ? JSON.parse(skills) : [];
      this.cache.certifications = certifications ? JSON.parse(certifications) : [];
      this.cache.workHistory = workHistory ? JSON.parse(workHistory) : [];
      this.cache.education = education ? JSON.parse(education) : [];
      this.cache.profile = profile ? JSON.parse(profile) : null;
      this.cache.resumes = resumes ? JSON.parse(resumes) : [];
      this.cache.resumeTemplates = resumeTemplates ? JSON.parse(resumeTemplates) : [];
      this.cache.lastSync = lastSync ? new Date(lastSync) : null;

      console.log('✅ Portfolio data loaded from storage');
    } catch (error) {
      console.error('❌ Error loading from storage:', error);
    }
  }

  /**
   * Save data to AsyncStorage
   */
  async saveToStorage() {
    try {
      await Promise.all([
        AsyncStorage.setItem('portfolio_projects', JSON.stringify(this.cache.projects)),
        AsyncStorage.setItem('portfolio_skills', JSON.stringify(this.cache.skills)),
        AsyncStorage.setItem('portfolio_certifications', JSON.stringify(this.cache.certifications)),
        AsyncStorage.setItem('portfolio_work_history', JSON.stringify(this.cache.workHistory)),
        AsyncStorage.setItem('portfolio_education', JSON.stringify(this.cache.education)),
        AsyncStorage.setItem('portfolio_profile', JSON.stringify(this.cache.profile)),
        AsyncStorage.setItem('portfolio_resumes', JSON.stringify(this.cache.resumes)),
        AsyncStorage.setItem('portfolio_resume_templates', JSON.stringify(this.cache.resumeTemplates)),
        AsyncStorage.setItem('portfolio_last_sync', new Date().toISOString())
      ]);

      this.cache.lastSync = new Date();
      console.log('✅ Portfolio data saved to storage');
    } catch (error) {
      console.error('❌ Error saving to storage:', error);
    }
  }

  /**
   * Set up auto-sync interval (more frequent for resume editing)
   */
  setupAutoSync() {
    // Auto-save every 30 seconds for resume editing
    setInterval(() => {
      if (!this.syncInProgress) {
        this.saveToStorage();
      }
    }, 30000);
  }

  /**
   * Event emitter functionality
   */
  emit(event, data) {
    if (this.subscribers[event]) {
      this.subscribers[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`❌ Error in event callback for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to events (alias for 'on' method)
   */
  subscribe(event, callback) {
    return this.on(event, callback);
  }

  /**
   * Subscribe to events
   */
  on(event, callback) {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }
    this.subscribers[event].push(callback);
    
    // Return unsubscribe function
    return () => {
      this.off(event, callback);
    };
  }

  /**
   * Unsubscribe from events
   */
  off(event, callback) {
    if (this.subscribers[event]) {
      this.subscribers[event] = this.subscribers[event].filter(cb => cb !== callback);
    }
  }

  // ==================== PROFILE METHODS (Enhanced for Resume) ====================

  /**
   * Get profile data
   */
  async getProfile() {
    try {
      return this.cache.profile;
    } catch (error) {
      console.error('❌ Error getting profile:', error);
      return null;
    }
  }

  /**
   * Update profile data
   */
  async updateProfile(profileData) {
    try {
      this.cache.profile = {
        ...this.cache.profile,
        ...profileData,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      this.emit('profile_updated', this.cache.profile);
      console.log('✅ Profile updated');
      
      return this.cache.profile;
    } catch (error) {
      console.error('❌ Error updating profile:', error);
      throw error;
    }
  }

  /**
   * Create profile if it doesn't exist
   */
  async createProfile(profileData) {
    try {
      this.cache.profile = {
        id: 'user_profile',
        ...profileData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      this.emit('profile_created', this.cache.profile);
      console.log('✅ Profile created');
      
      return this.cache.profile;
    } catch (error) {
      console.error('❌ Error creating profile:', error);
      throw error;
    }
  }

  // ==================== RESUME METHODS ====================

  /**
   * Generate resume data from portfolio
   */
  async generateResumeFromPortfolio(resumeId = null) {
    try {
      console.log('🔄 Generating resume from portfolio data...');
      
      const resumeData = {
        id: resumeId || `resume_${Date.now()}`,
        personalInfo: {
          name: this.cache.profile?.name || '',
          email: this.cache.profile?.email || '',
          phone: this.cache.profile?.phone || '',
          location: this.cache.profile?.location || '',
          summary: this.cache.profile?.bio || '',
          linkedIn: this.cache.profile?.linkedIn || '',
          website: this.cache.profile?.website || '',
          github: this.cache.profile?.github || '',
        },
        experience: this.cache.workHistory.map(work => ({
          id: work.id,
          title: work.position,
          company: work.company,
          duration: `${work.startDate} - ${work.endDate || 'Present'}`,
          description: work.description || `${work.position} at ${work.company}`,
          achievements: work.achievements || [],
          location: work.location || '',
          employmentType: work.employmentType || 'Full-time',
        })),
        education: this.cache.education.map(edu => ({
          id: edu.id,
          degree: edu.degree,
          institution: edu.institution,
          year: edu.endDate || edu.startDate,
          fieldOfStudy: edu.fieldOfStudy,
          gpa: edu.gpa,
          location: edu.location,
          status: edu.status,
        })),
        skills: this.cache.skills.map(skill => skill.name),
        skillsDetailed: this.cache.skills.map(skill => ({
          name: skill.name,
          proficiency: skill.proficiency,
          category: skill.category,
          experienceYears: skill.experienceYears,
        })),
        projects: this.cache.projects.map(project => ({
          id: project.id,
          title: project.title,
          description: project.description,
          technologies: project.technologies || [],
          status: project.status,
          category: project.category,
          startDate: project.startDate,
          endDate: project.endDate,
          url: project.url,
          achievements: project.achievements || [],
        })),
        certifications: this.cache.certifications.map(cert => ({
          id: cert.id,
          title: cert.title,
          issuer: cert.issuer,
          date: cert.date,
          expiryDate: cert.expiryDate,
          credentialId: cert.credentialId,
          verificationUrl: cert.verificationUrl,
          status: cert.status,
        })),
        lastUpdated: new Date().toISOString(),
        source: 'portfolio_integrated',
        template: 'modern',
        version: '1.0'
      };

      console.log('✅ Resume generated from portfolio data');
      return resumeData;
    } catch (error) {
      console.error('❌ Error generating resume from portfolio:', error);
      throw error;
    }
  }

  /**
   * Save resume data
   */
  async saveResume(resumeData) {
    try {
      console.log('🔄 Saving resume data...');
      
      const existingIndex = this.cache.resumes.findIndex(resume => resume.id === resumeData.id);
      
      const resumeToSave = {
        ...resumeData,
        lastUpdated: new Date().toISOString()
      };

      if (existingIndex >= 0) {
        this.cache.resumes[existingIndex] = resumeToSave;
      } else {
        this.cache.resumes.push(resumeToSave);
      }

      await this.saveToStorage();
      this.emit('resume_saved', resumeToSave);
      console.log('✅ Resume saved successfully');
      
      return resumeToSave;
    } catch (error) {
      console.error('❌ Error saving resume:', error);
      throw error;
    }
  }

  /**
   * Get resume by ID
   */
  async getResume(resumeId) {
    try {
      const resume = this.cache.resumes.find(resume => resume.id === resumeId);
      return resume || null;
    } catch (error) {
      console.error('❌ Error getting resume:', error);
      return null;
    }
  }

  /**
   * Get all resumes
   */
  async getResumes() {
    try {
      return this.cache.resumes || [];
    } catch (error) {
      console.error('❌ Error getting resumes:', error);
      return [];
    }
  }

  /**
   * Delete resume
   */
  async deleteResume(resumeId) {
    try {
      const index = this.cache.resumes.findIndex(resume => resume.id === resumeId);
      if (index >= 0) {
        const deletedResume = this.cache.resumes.splice(index, 1)[0];
        await this.saveToStorage();
        this.emit('resume_deleted', deletedResume);
        console.log('✅ Resume deleted');
        return deletedResume;
      }
      return null;
    } catch (error) {
      console.error('❌ Error deleting resume:', error);
      throw error;
    }
  }

  /**
   * Sync resume changes back to portfolio
   */
  async syncResumeToPortfolio(resumeData) {
    try {
      console.log('🔄 Syncing resume changes back to portfolio...');
      
      // Update profile if changed
      if (resumeData.personalInfo) {
        const profileUpdate = {
          name: resumeData.personalInfo.name,
          email: resumeData.personalInfo.email,
          phone: resumeData.personalInfo.phone,
          location: resumeData.personalInfo.location,
          bio: resumeData.personalInfo.summary,
          linkedIn: resumeData.personalInfo.linkedIn,
          website: resumeData.personalInfo.website,
          github: resumeData.personalInfo.github,
        };
        
        await this.updateProfile(profileUpdate);
      }

      console.log('✅ Resume changes synced back to portfolio');
      return true;
    } catch (error) {
      console.error('❌ Error syncing resume to portfolio:', error);
      return false;
    }
  }

  // ==================== EXISTING METHODS (Projects, Skills, etc.) ====================

  /**
   * Get all projects
   */
  async getProjects(filters = {}) {
    try {
      let projects = [...this.cache.projects];

      // Apply filters
      if (filters.category) {
        projects = projects.filter(project => project.category === filters.category);
      }
      if (filters.status) {
        projects = projects.filter(project => project.status === filters.status);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        projects = projects.filter(project => 
          project.title.toLowerCase().includes(searchLower) ||
          project.description.toLowerCase().includes(searchLower)
        );
      }

      // Sort by date (newest first)
      projects.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      return projects;
    } catch (error) {
      console.error('❌ Error getting projects:', error);
      return [];
    }
  }

  /**
   * Add a new project
   */
  async addProject(projectData) {
    try {
      const project = {
        id: Date.now().toString(),
        ...projectData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.cache.projects.push(project);
      await this.saveToStorage();
      
      this.emit('project_added', project);
      console.log('✅ Project added:', project.title);
      
      return project;
    } catch (error) {
      console.error('❌ Error adding project:', error);
      throw error;
    }
  }

  /**
   * Update a project
   */
  async updateProject(id, updates) {
    try {
      const index = this.cache.projects.findIndex(project => project.id === id);
      if (index === -1) {
        throw new Error('Project not found');
      }

      this.cache.projects[index] = {
        ...this.cache.projects[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      
      this.emit('project_updated', this.cache.projects[index]);
      console.log('✅ Project updated:', id);
      
      return this.cache.projects[index];
    } catch (error) {
      console.error('❌ Error updating project:', error);
      throw error;
    }
  }

  /**
   * Delete a project
   */
  async deleteProject(id) {
    try {
      const index = this.cache.projects.findIndex(project => project.id === id);
      if (index === -1) {
        throw new Error('Project not found');
      }

      const deletedProject = this.cache.projects.splice(index, 1)[0];
      await this.saveToStorage();
      
      this.emit('project_deleted', deletedProject);
      console.log('✅ Project deleted:', id);
      
      return deletedProject;
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      throw error;
    }
  }

  // ==================== SKILLS METHODS ====================

  /**
   * Get all skills
   */
  async getSkills(filters = {}) {
    try {
      let skills = [...this.cache.skills];

      // Apply filters
      if (filters.category) {
        skills = skills.filter(skill => skill.category === filters.category);
      }
      if (filters.proficiency) {
        skills = skills.filter(skill => skill.proficiency >= filters.proficiency);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        skills = skills.filter(skill => 
          skill.name.toLowerCase().includes(searchLower)
        );
      }

      // Sort by proficiency (highest first)
      skills.sort((a, b) => b.proficiency - a.proficiency);

      return skills;
    } catch (error) {
      console.error('❌ Error getting skills:', error);
      return [];
    }
  }

  /**
   * Add a new skill
   */
  async addSkill(skillData) {
    try {
      const skill = {
        id: Date.now().toString(),
        ...skillData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.cache.skills.push(skill);
      await this.saveToStorage();
      
      this.emit('skill_added', skill);
      console.log('✅ Skill added:', skill.name);
      
      return skill;
    } catch (error) {
      console.error('❌ Error adding skill:', error);
      throw error;
    }
  }

  /**
   * Update a skill
   */
  async updateSkill(id, updates) {
    try {
      const index = this.cache.skills.findIndex(skill => skill.id === id);
      if (index === -1) {
        throw new Error('Skill not found');
      }

      this.cache.skills[index] = {
        ...this.cache.skills[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      
      this.emit('skill_updated', this.cache.skills[index]);
      console.log('✅ Skill updated:', id);
      
      return this.cache.skills[index];
    } catch (error) {
      console.error('❌ Error updating skill:', error);
      throw error;
    }
  }

  /**
   * Delete a skill
   */
  async deleteSkill(id) {
    try {
      const index = this.cache.skills.findIndex(skill => skill.id === id);
      if (index === -1) {
        throw new Error('Skill not found');
      }

      const deletedSkill = this.cache.skills.splice(index, 1)[0];
      await this.saveToStorage();
      
      this.emit('skill_deleted', deletedSkill);
      console.log('✅ Skill deleted:', id);
      
      return deletedSkill;
    } catch (error) {
      console.error('❌ Error deleting skill:', error);
      throw error;
    }
  }

  // ==================== CERTIFICATIONS METHODS ====================

  /**
   * Get all certifications
   */
  async getCertifications(filters = {}) {
    try {
      let certifications = [...this.cache.certifications];

      // Apply filters
      if (filters.status) {
        certifications = certifications.filter(cert => cert.status === filters.status);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        certifications = certifications.filter(cert => 
          cert.title.toLowerCase().includes(searchLower) ||
          cert.issuer.toLowerCase().includes(searchLower)
        );
      }

      // Sort by date (newest first)
      certifications.sort((a, b) => new Date(b.date) - new Date(a.date));

      return certifications;
    } catch (error) {
      console.error('❌ Error getting certifications:', error);
      return [];
    }
  }

  /**
   * Add a new certification
   */
  async addCertification(certificationData) {
    try {
      const certification = {
        id: Date.now().toString(),
        ...certificationData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.cache.certifications.push(certification);
      await this.saveToStorage();
      
      this.emit('certification_added', certification);
      console.log('✅ Certification added:', certification.title);
      
      return certification;
    } catch (error) {
      console.error('❌ Error adding certification:', error);
      throw error;
    }
  }

  /**
   * Update a certification
   */
  async updateCertification(id, updates) {
    try {
      const index = this.cache.certifications.findIndex(cert => cert.id === id);
      if (index === -1) {
        throw new Error('Certification not found');
      }

      this.cache.certifications[index] = {
        ...this.cache.certifications[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      
      this.emit('certification_updated', this.cache.certifications[index]);
      console.log('✅ Certification updated:', id);
      
      return this.cache.certifications[index];
    } catch (error) {
      console.error('❌ Error updating certification:', error);
      throw error;
    }
  }

  /**
   * Delete a certification
   */
  async deleteCertification(id) {
    try {
      const index = this.cache.certifications.findIndex(cert => cert.id === id);
      if (index === -1) {
        throw new Error('Certification not found');
      }

      const deletedCertification = this.cache.certifications.splice(index, 1)[0];
      await this.saveToStorage();
      
      this.emit('certification_deleted', deletedCertification);
      console.log('✅ Certification deleted:', id);
      
      return deletedCertification;
    } catch (error) {
      console.error('❌ Error deleting certification:', error);
      throw error;
    }
  }

  // ==================== WORK HISTORY METHODS ====================

  /**
   * Get all work history
   */
  async getWorkHistory(filters = {}) {
    try {
      let workHistory = [...this.cache.workHistory];

      // Apply filters
      if (filters.employmentType) {
        workHistory = workHistory.filter(work => work.employmentType === filters.employmentType);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        workHistory = workHistory.filter(work => 
          work.position.toLowerCase().includes(searchLower) ||
          work.company.toLowerCase().includes(searchLower)
        );
      }

      // Sort by start date (newest first)
      workHistory.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

      return workHistory;
    } catch (error) {
      console.error('❌ Error getting work history:', error);
      return [];
    }
  }

  /**
   * Add new work history
   */
  async addWorkHistory(workData) {
    try {
      const work = {
        id: Date.now().toString(),
        ...workData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.cache.workHistory.push(work);
      await this.saveToStorage();
      
      this.emit('work_history_added', work);
      console.log('✅ Work history added:', work.position);
      
      return work;
    } catch (error) {
      console.error('❌ Error adding work history:', error);
      throw error;
    }
  }

  /**
   * Update work history
   */
  async updateWorkHistory(id, updates) {
    try {
      const index = this.cache.workHistory.findIndex(work => work.id === id);
      if (index === -1) {
        throw new Error('Work history not found');
      }

      this.cache.workHistory[index] = {
        ...this.cache.workHistory[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      
      this.emit('work_history_updated', this.cache.workHistory[index]);
      console.log('✅ Work history updated:', id);
      
      return this.cache.workHistory[index];
    } catch (error) {
      console.error('❌ Error updating work history:', error);
      throw error;
    }
  }

  /**
   * Delete work history
   */
  async deleteWorkHistory(id) {
    try {
      const index = this.cache.workHistory.findIndex(work => work.id === id);
      if (index === -1) {
        throw new Error('Work history not found');
      }

      const deletedWork = this.cache.workHistory.splice(index, 1)[0];
      await this.saveToStorage();
      
      this.emit('work_history_deleted', deletedWork);
      console.log('✅ Work history deleted:', id);
      
      return deletedWork;
    } catch (error) {
      console.error('❌ Error deleting work history:', error);
      throw error;
    }
  }

  // ==================== EDUCATION METHODS ====================

  /**
   * Get all education
   */
  async getEducation(filters = {}) {
    try {
      let education = [...this.cache.education];

      // Apply filters
      if (filters.level) {
        education = education.filter(edu => edu.level === filters.level);
      }
      if (filters.status) {
        education = education.filter(edu => edu.status === filters.status);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        education = education.filter(edu => 
          edu.degree.toLowerCase().includes(searchLower) ||
          edu.institution.toLowerCase().includes(searchLower) ||
          edu.fieldOfStudy.toLowerCase().includes(searchLower)
        );
      }

      // Sort by start date (newest first)
      education.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

      return education;
    } catch (error) {
      console.error('❌ Error getting education:', error);
      return [];
    }
  }

  /**
   * Add new education
   */
  async addEducation(educationData) {
    try {
      const education = {
        id: Date.now().toString(),
        ...educationData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      this.cache.education.push(education);
      await this.saveToStorage();
      
      this.emit('education_added', education);
      console.log('✅ Education added:', education.degree);
      
      return education;
    } catch (error) {
      console.error('❌ Error adding education:', error);
      throw error;
    }
  }

  /**
   * Update education
   */
  async updateEducation(id, updates) {
    try {
      const index = this.cache.education.findIndex(edu => edu.id === id);
      if (index === -1) {
        throw new Error('Education not found');
      }

      this.cache.education[index] = {
        ...this.cache.education[index],
        ...updates,
        updatedAt: new Date().toISOString()
      };

      await this.saveToStorage();
      
      this.emit('education_updated', this.cache.education[index]);
      console.log('✅ Education updated:', id);
      
      return this.cache.education[index];
    } catch (error) {
      console.error('❌ Error updating education:', error);
      throw error;
    }
  }

  /**
   * Delete education
   */
  async deleteEducation(id) {
    try {
      const index = this.cache.education.findIndex(edu => edu.id === id);
      if (index === -1) {
        throw new Error('Education not found');
      }

      const deletedEducation = this.cache.education.splice(index, 1)[0];
      await this.saveToStorage();
      
      this.emit('education_deleted', deletedEducation);
      console.log('✅ Education deleted:', id);
      
      return deletedEducation;
    } catch (error) {
      console.error('❌ Error deleting education:', error);
      throw error;
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Search across all portfolio data
   */
  async searchAll(query) {
    try {
      const searchLower = query.toLowerCase();
      const results = {
        projects: [],
        skills: [],
        certifications: [],
        workHistory: [],
        education: [],
        resumes: []
      };

      // Search projects
      results.projects = this.cache.projects.filter(project =>
        project.title.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower)
      );

      // Search skills
      results.skills = this.cache.skills.filter(skill =>
        skill.name.toLowerCase().includes(searchLower)
      );

      // Search certifications
      results.certifications = this.cache.certifications.filter(cert =>
        cert.title.toLowerCase().includes(searchLower) ||
        cert.issuer.toLowerCase().includes(searchLower)
      );

      // Search work history
      results.workHistory = this.cache.workHistory.filter(work =>
        work.position.toLowerCase().includes(searchLower) ||
        work.company.toLowerCase().includes(searchLower)
      );

      // Search education
      results.education = this.cache.education.filter(edu =>
        edu.degree.toLowerCase().includes(searchLower) ||
        edu.institution.toLowerCase().includes(searchLower) ||
        edu.fieldOfStudy.toLowerCase().includes(searchLower)
      );

      // Search resumes
      results.resumes = this.cache.resumes.filter(resume =>
        resume.personalInfo?.name?.toLowerCase().includes(searchLower) ||
        resume.personalInfo?.summary?.toLowerCase().includes(searchLower)
      );

      return results;
    } catch (error) {
      console.error('❌ Error searching portfolio:', error);
      return { projects: [], skills: [], certifications: [], workHistory: [], education: [], resumes: [] };
    }
  }

  /**
   * Get portfolio statistics
   */
  async getStatistics() {
    try {
      const stats = {
        totalProjects: this.cache.projects.length,
        completedProjects: this.cache.projects.filter(p => p.status === 'completed').length,
        totalSkills: this.cache.skills.length,
        expertSkills: this.cache.skills.filter(s => s.proficiency >= 80).length,
        totalCertifications: this.cache.certifications.length,
        activeCertifications: this.cache.certifications.filter(c => c.status === 'active').length,
        totalWorkExperience: this.cache.workHistory.length,
        totalEducation: this.cache.education.length,
        completedEducation: this.cache.education.filter(e => e.status === 'completed').length,
        totalResumes: this.cache.resumes.length,
        lastUpdated: this.cache.lastSync,
        profileCompleteness: this.calculateProfileCompleteness()
      };

      return stats;
    } catch (error) {
      console.error('❌ Error getting statistics:', error);
      return {};
    }
  }

  /**
   * Calculate profile completeness percentage
   */
  calculateProfileCompleteness() {
    if (!this.cache.profile) return 0;
    
    const fields = ['name', 'email', 'phone', 'location', 'bio'];
    const completedFields = fields.filter(field => 
      this.cache.profile[field] && this.cache.profile[field].trim()
    );
    
    const baseScore = (completedFields.length / fields.length) * 60;
    
    // Add bonus points for additional data
    let bonusScore = 0;
    if (this.cache.projects.length > 0) bonusScore += 10;
    if (this.cache.skills.length > 0) bonusScore += 10;
    if (this.cache.workHistory.length > 0) bonusScore += 10;
    if (this.cache.education.length > 0) bonusScore += 5;
    if (this.cache.certifications.length > 0) bonusScore += 5;
    
    return Math.min(Math.round(baseScore + bonusScore), 100);
  }

  /**
   * Export all portfolio data
   */
  async exportData(format = 'json') {
    try {
      const data = {
        projects: this.cache.projects,
        skills: this.cache.skills,
        certifications: this.cache.certifications,
        workHistory: this.cache.workHistory,
        education: this.cache.education,
        profile: this.cache.profile,
        resumes: this.cache.resumes,
        exportDate: new Date().toISOString(),
        version: '2.0'
      };

      if (format === 'json') {
        return JSON.stringify(data, null, 2);
      }

      return data;
    } catch (error) {
      console.error('❌ Error exporting data:', error);
      throw error;
    }
  }

  /**
   * Import portfolio data
   */
  async importData(data) {
    try {
      if (typeof data === 'string') {
        data = JSON.parse(data);
      }

      // Validate data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid data format');
      }

      // Import data with validation
      if (data.projects && Array.isArray(data.projects)) {
        this.cache.projects = data.projects;
      }
      if (data.skills && Array.isArray(data.skills)) {
        this.cache.skills = data.skills;
      }
      if (data.certifications && Array.isArray(data.certifications)) {
        this.cache.certifications = data.certifications;
      }
      if (data.workHistory && Array.isArray(data.workHistory)) {
        this.cache.workHistory = data.workHistory;
      }
      if (data.education && Array.isArray(data.education)) {
        this.cache.education = data.education;
      }
      if (data.profile) {
        this.cache.profile = data.profile;
      }
      if (data.resumes && Array.isArray(data.resumes)) {
        this.cache.resumes = data.resumes;
      }

      await this.saveToStorage();
      
      this.emit('data_imported', data);
      console.log('✅ Portfolio data imported successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Error importing data:', error);
      throw error;
    }
  }

  /**
   * Clear all portfolio data
   */
  async clearAllData() {
    try {
      this.cache = {
        projects: [],
        skills: [],
        certifications: [],
        workHistory: [],
        education: [],
        profile: null,
        resumes: [],
        resumeTemplates: [],
        lastSync: null
      };

      await Promise.all([
        AsyncStorage.removeItem('portfolio_projects'),
        AsyncStorage.removeItem('portfolio_skills'),
        AsyncStorage.removeItem('portfolio_certifications'),
        AsyncStorage.removeItem('portfolio_work_history'),
        AsyncStorage.removeItem('portfolio_education'),
        AsyncStorage.removeItem('portfolio_profile'),
        AsyncStorage.removeItem('portfolio_resumes'),
        AsyncStorage.removeItem('portfolio_resume_templates'),
        AsyncStorage.removeItem('portfolio_last_sync')
      ]);

      this.emit('data_cleared', {});
      console.log('✅ All portfolio data cleared');
      
      return true;
    } catch (error) {
      console.error('❌ Error clearing data:', error);
      return false;
    }
  }
}

// Create and export singleton instance
const portfolioDatabase = new PortfolioDatabase();

// Export both the class and the instance for flexibility
export { PortfolioDatabase };
export default portfolioDatabase;

